/**
 * Centralized API client with authentication
 * Extracted from duplicate implementations in poService, salesService, etc.
 */

// Tauri desktop app uses absolute URL to production API; web uses relative (same-origin) paths
const IS_TAURI = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
export const API_BASE = IS_TAURI ? 'https://www.fluxconn.com' : '';

/**
 * Error response from API
 */
export interface ApiError {
  error: string;
  code?: string;
  details?: unknown;
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Request options extending standard RequestInit
 */
export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

/**
 * Perform an authenticated fetch request
 * - Automatically adds Content-Type and Authorization headers
 * - Throws ApiClientError on non-OK responses
 *
 * @param url - The URL to fetch
 * @param options - Request options
 * @returns The Response object
 */
export async function fetchWithAuth(url: string, options: ApiRequestOptions = {}): Promise<Response> {
  const { body, skipAuth = false, headers: customHeaders, ...restOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (!skipAuth) {
    const token = localStorage.getItem('auth_token');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  const fetchOptions: RequestInit = {
    ...restOptions,
    headers,
  };

  if (body !== undefined) {
    fetchOptions.body = JSON.stringify(body);
  }

  const resolvedUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
  const response = await fetch(resolvedUrl, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' })) as ApiError;
    throw new ApiClientError(
      errorData.error || `HTTP ${response.status}`,
      response.status,
      errorData.code,
      errorData.details
    );
  }

  return response;
}

/**
 * Perform a GET request and parse JSON response
 */
export async function get<T>(url: string, options?: ApiRequestOptions): Promise<T> {
  const response = await fetchWithAuth(url, { ...options, method: 'GET' });
  return response.json();
}

/**
 * Perform a POST request and parse JSON response
 */
export async function post<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  const response = await fetchWithAuth(url, { ...options, method: 'POST', body });
  return response.json();
}

/**
 * Perform a PUT request and parse JSON response
 */
export async function put<T>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  const response = await fetchWithAuth(url, { ...options, method: 'PUT', body });
  return response.json();
}

/**
 * Perform a PATCH request and optionally parse JSON response
 */
export async function patch<T = void>(url: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  const response = await fetchWithAuth(url, { ...options, method: 'PATCH', body });
  // Some PATCH requests return empty body
  const text = await response.text();
  return text ? JSON.parse(text) : (undefined as T);
}

/**
 * Perform a DELETE request
 */
export async function del(url: string, options?: ApiRequestOptions): Promise<void> {
  await fetchWithAuth(url, { ...options, method: 'DELETE' });
}

/**
 * Build URL search params from a filter object
 * Handles common filter types: strings, numbers, dates, booleans
 *
 * @param filter - Filter object with optional properties
 * @returns URLSearchParams instance
 */
export function buildFilterParams<T extends Record<string, unknown>>(filter?: T): URLSearchParams {
  const params = new URLSearchParams();

  if (!filter) return params;

  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === null || value === '') continue;

    if (typeof value === 'boolean') {
      params.append(key, value.toString());
    } else if (typeof value === 'number') {
      params.append(key, value.toString());
    } else if (typeof value === 'string') {
      params.append(key, value);
    } else if (Array.isArray(value)) {
      value.forEach(v => params.append(key, String(v)));
    }
  }

  return params;
}

/**
 * Build a URL with optional filter parameters
 *
 * @param baseUrl - The base URL
 * @param filter - Optional filter object
 * @returns URL string with query parameters
 */
export function buildUrl<T extends Record<string, unknown>>(baseUrl: string, filter?: T): string {
  const params = buildFilterParams(filter);
  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Create a typed API client for a specific resource
 *
 * @param baseUrl - The base URL for the resource
 * @returns Object with CRUD methods
 */
export function createApiClient<
  TEntity,
  TCreate = Partial<TEntity>,
  TUpdate = Partial<TEntity>,
  TList = { items: TEntity[]; total: number; limit: number; offset: number },
  TFilter extends Record<string, unknown> = Record<string, unknown>
>(baseUrl: string) {
  return {
    list: (filter?: TFilter): Promise<TList> => get<TList>(buildUrl(baseUrl, filter)),
    get: (id: string): Promise<TEntity> => get<TEntity>(`${baseUrl}/${id}`),
    create: (data: TCreate): Promise<TEntity> => post<TEntity>(baseUrl, data),
    update: (id: string, data: TUpdate): Promise<TEntity> => put<TEntity>(`${baseUrl}/${id}`, data),
    delete: (id: string): Promise<void> => del(`${baseUrl}/${id}`),
  };
}
